import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';

interface ProductImage {
  url: string;
  alt: string;
}

const ProductCoeurBlancLivrePlie = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showNotification, setShowNotification] = useState(false);

  const product = {
    id: 'coeur-blanc-livre-plie',
    title: 'Cœur blanc – Livre plié (pièce unique)',
    price: 30,
    stock: 1,
    category: 'Livres pliés',
    collections: ['Cadeaux qui ont du sens', 'Symboles & spiritualité douce', 'Créations en livres pliés'],
    shortDescription: 'Un grand cœur sculpté dans les pages d\'un livre, pour offrir un message d\'amour tout en douceur. Pièce unique, faite main.',
    images: [
      {
        url: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/8e52fd41d5707d37aab1d9cca97e0148.png',
        alt: 'Cœur blanc en livre plié - Vue principale'
      }
    ],
    isExclusivePreview: true,
    badges: ['Pièce unique', 'Avant-première']
  };

  const handleAddToCart = () => {
    addToCart({
      id: product.id,
      title: product.title,
      price: product.price,
      quantity: quantity,
      image: product.images[0].url,
      maxStock: product.stock
    });
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    setTimeout(() => navigate('/panier'), 300);
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    // Mise à jour des métadonnées SEO
    document.title = 'Livre plié cœur – Pièce unique faite main | Les Carnets de Lolotte';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Grand cœur en livre plié, pièce unique artisanale réalisée à la main à partir d\'un livre recyclé. Une décoration poétique et un cadeau plein de sens.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'livre plié cœur, art en papier, décoration romantique, cadeau symbolique, pièce unique, upcycling');
    }

    // Open Graph
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Livre plié cœur – Pièce unique faite main | Les Carnets de Lolotte');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Grand cœur en livre plié, pièce unique artisanale réalisée à la main à partir d\'un livre recyclé. Une décoration poétique et un cadeau plein de sens.');
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', product.images[0].url);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://lescarnetsdelolotte.com/produit-coeur-blanc-livre-plie');
    }

    // JSON-LD
    const jsonLd = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": product.title,
      "image": product.images.map(img => img.url),
      "description": product.shortDescription,
      "brand": {
        "@type": "Brand",
        "name": "Les Carnets de Lolotte"
      },
      "offers": {
        "@type": "Offer",
        "url": `https://lescarnetsdelolotte.com/produit-coeur-blanc-livre-plie`,
        "priceCurrency": "EUR",
        "price": product.price,
        "availability": "https://schema.org/InStock",
        "itemCondition": "https://schema.org/NewCondition"
      }
    };

    let scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(jsonLd);

    return () => {
      // Nettoyage optionnel
    };
  }, [product]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-pink-50">
      <Header />

      {showNotification && (
        <div className="fixed top-24 right-6 bg-pink-600 text-white px-6 py-4 rounded-lg shadow-2xl z-50 animate-fade-in flex items-center gap-3">
          <i className="ri-check-line text-2xl"></i>
          <span className="font-medium">Produit ajouté au panier !</span>
        </div>
      )}

      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* Galerie d'images */}
            <div className="space-y-4">
              <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg aspect-square">
                <img
                  src={product.images[selectedImage].url}
                  alt={product.images[selectedImage].alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  {product.badges.map((badge, index) => (
                    <span
                      key={index}
                      className="bg-pink-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg"
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Informations produit */}
            <div className="space-y-8">
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {product.title}
                </h1>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {product.shortDescription}
                </p>
              </div>

              <div className="flex items-baseline gap-4">
                <span className="text-5xl font-bold text-pink-600">
                  {product.price} €
                </span>
              </div>

              <div className="bg-pink-50 border-2 border-pink-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <i className="ri-star-fill text-pink-600 text-2xl"></i>
                  <span className="text-lg font-semibold text-gray-900">Pièce unique</span>
                </div>
                <p className="text-gray-700">
                  Cette création est unique. Une fois vendue, elle ne sera plus disponible.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-gray-700 font-medium">Quantité :</label>
                  <div className="flex items-center gap-3 bg-white border-2 border-gray-200 rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-4 py-2 text-gray-600 hover:text-pink-600 transition-colors"
                      disabled={quantity <= 1}
                    >
                      <i className="ri-subtract-line text-xl"></i>
                    </button>
                    <span className="text-lg font-semibold text-gray-900 min-w-[2rem] text-center">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="px-4 py-2 text-gray-600 hover:text-pink-600 transition-colors"
                      disabled={quantity >= product.stock}
                    >
                      <i className="ri-add-line text-xl"></i>
                    </button>
                  </div>
                  <span className="text-sm text-gray-500">
                    (Stock : {product.stock})
                  </span>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 bg-white border-2 border-pink-600 text-pink-600 px-8 py-4 rounded-lg font-semibold hover:bg-pink-50 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-shopping-cart-line text-xl"></i>
                    Ajouter au panier
                  </button>
                  <button
                    onClick={handleBuyNow}
                    className="flex-1 bg-pink-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-pink-700 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-flashlight-fill text-xl"></i>
                    Acheter maintenant
                  </button>
                </div>
              </div>

              <div className="border-t-2 border-gray-200 pt-6 space-y-3">
                <div className="flex items-center gap-3 text-gray-700">
                  <i className="ri-truck-line text-pink-600 text-xl"></i>
                  <span>Livraison soignée et sécurisée</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <i className="ri-gift-line text-pink-600 text-xl"></i>
                  <span>Emballage cadeau inclus</span>
                </div>
                <div className="flex items-center gap-3 text-gray-700">
                  <i className="ri-hand-heart-line text-pink-600 text-xl"></i>
                  <span>Création artisanale française</span>
                </div>
              </div>
            </div>
          </div>

          {/* Description détaillée */}
          <div className="mt-20 max-w-4xl mx-auto space-y-12">
            
            <section className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                <i className="ri-sparkling-line text-pink-600"></i>
                Une pièce unique, pleine de sens
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Ce cœur a été entièrement réalisé à la main, page après page, à partir d'un livre recyclé. Une création poétique qui symbolise l'amour, l'attachement, la tendresse… et les souvenirs qu'on garde précieusement.
              </p>
            </section>

            <section className="bg-pink-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                <i className="ri-bookmark-line text-pink-600"></i>
                Détails
              </h2>
              <ul className="space-y-4 text-lg text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-pink-600 text-xl mt-1"></i>
                  <span>Livre ancien plié à la main (upcycling)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-pink-600 text-xl mt-1"></i>
                  <span>Forme : grand cœur</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-pink-600 text-xl mt-1"></i>
                  <span>Petite décoration en papier (fleur/rosette) incluse</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-pink-600 text-xl mt-1"></i>
                  <span>Chaque création est unique : de légères variations sont normales et font son charme</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-pink-600 text-xl mt-1"></i>
                  <span>Emballage soigné, prêt à offrir</span>
                </li>
              </ul>
            </section>

            <section className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                <i className="ri-gift-2-line text-pink-600"></i>
                Idées cadeau
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Saint-Valentin, anniversaire, mariage, fiançailles, cadeau symbolique, décoration romantique, souvenir d'un moment important.
              </p>
            </section>

            <section className="bg-pink-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                <i className="ri-ruler-line text-pink-600"></i>
                Dimensions
              </h2>
              <p className="text-lg text-gray-700">
                Dimensions : à renseigner
              </p>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductCoeurBlancLivrePlie;
